/**
 * Curated AI prompt suggestions per SPA page. Phase 12.5.
 *
 * Each page gets 4–6 domain-specific prompts. The SuggestedPromptsTile
 * deep-links to /ai?prompt=<urlencoded> which pre-fills the composer.
 *
 * Convention: phrase prompts as questions the user would naturally ask
 * while looking at THIS page. Avoid generic prompts ("how am I doing?")
 * — those belong on /dashboard which gets the `getEverything` meta-tool
 * surfaced via a "what's my financial picture?" prompt.
 */

export const PAGE_PROMPTS = {
  dashboard: [
    "Show me an everything snapshot.",
    "What's my biggest unaddressed financial risk right now?",
    "Are any of my schedules or bills going to surprise me this month?",
    "What's a 'yes' I could give myself in the next 90 days?",
    "Where should the next $10k go?",
    "Do I have any alerts I should look at?",
    "Is my saving rate slipping? Am I on track to hit FI?",
    "Why is any of my spending growing faster than my income?",
  ],
  accounts: [
    "What are my top 5 accounts by balance?",
    "Are any of my external connections broken or in needs_reauth?",
    "Show me my credit card utilization across all cards.",
    "Which account should I direct-deposit my next paycheck to?",
  ],
  bills: [
    "What bills are coming due this week?",
    "What are my fixed monthly bill commitments?",
    "Has any subscription drifted up in price recently?",
    "What's my biggest spend category among recurring bills?",
  ],
  budget: [
    "Am I over budget on any category this month?",
    "How am I doing this month vs my budget caps?",
    "Where did the money go in the last 30 days?",
    "What's my spending trend over the last 6 months?",
  ],
  spending: [
    "What's my biggest spend category this month?",
    "Is my spending growing year-over-year?",
    "Which merchants did I spend the most at last month?",
    "What's my spending trend?",
  ],
  insurance: [
    "Am I underinsured on term life?",
    "What's my disability income-replacement gap?",
    "Do I have enough umbrella coverage for my net worth?",
    "Which policies are coming up for renewal?",
  ],
  documents: [
    "What documents are expiring in the next 90 days?",
    "When does my passport expire?",
    "Which documents have I never reviewed?",
    "What HSA receipts haven't been OCR'd yet?",
  ],
  estate: [
    "Are my estate documents in order?",
    "When did I last review my will?",
    "Which beneficiary designations are missing or stale?",
    "What's on my estate readiness checklist?",
  ],
  portfolio: [
    "What's my biggest concentration risk?",
    "How does my portfolio overlap across funds?",
    "What's my tax-cost ratio drag this year?",
    "Compare my returns vs the S&P 500 over the last year.",
    "What's my expense-ratio drag?",
    "Should I rebalance? What's the cash-inflow plan?",
  ],
  portfolioRebalance: [
    "What's my optimal rebalance using only new cash?",
    "If I had $20k to deploy, where would it go?",
    "Which sleeves are most drift-flagged right now?",
  ],
  portfolioStressTest: [
    "How would my portfolio do in a 2008-style crash?",
    "What if inflation hits 7% again like 2022?",
    "Stress test against a 1970s stagflation scenario.",
  ],
  scenarios: [
    "What if we moved to a lower cost-of-living city?",
    "What if one of us cut hours to 60% time?",
    "Replay the great financial crisis on my current portfolio.",
  ],
  fire: [
    "When can we retire?",
    "What's my Coast FI number?",
    "What's the Trinity-study success rate at my current spend?",
    "What's my Monte Carlo retirement probability?",
  ],
  fireContributionPriority: [
    "Where should my next dollar go?",
    "Am I capturing my full 401(k) employer match?",
    "Should I prioritize HSA or Roth IRA this year?",
    "What's my mega-backdoor capacity given my plan?",
  ],
  tax: [
    "What's my projected Q3 tax liability?",
    "Will I trigger AMT this year?",
    "Is my Roth conversion ladder optimal?",
    "What's my safe-harbor status for federal estimated taxes?",
  ],
  cards: [
    "Am I using the right card for groceries?",
    "What's my optimal card portfolio?",
    "Compare Sapphire Reserve vs Amex Gold for travel.",
    "How much in rewards am I leaving on the table?",
  ],
  credit: [
    "What's my credit score trend?",
    "What's my credit utilization across all cards?",
    "Should I pay down a card balance to improve my FICO?",
  ],
  integrations: [
    "Which connections are broken?",
    "When did each connection last sync?",
    "Are any staging transactions waiting for review?",
  ],
  journal: [
    "What did I write last week?",
    "Show me my recent journal entries.",
    "Which decisions are flagged for revisit?",
  ],
  alerts: [
    "Do I have any unacknowledged alerts?",
    "What alerts have fired in the last 14 days?",
    "Show me critical-severity alerts only.",
  ],
  housing: [
    "What's my mortgage payoff projection?",
    "How much am I spending monthly on housing?",
    "Should I refinance given current rates?",
  ],
  collegePlans: [
    "Will my 529 cover full in-state tuition?",
    "What's the projected gap if my daughter goes to a private school?",
    "How much should I contribute monthly to hit our target?",
  ],
  equityGrants: [
    "When does my next vest happen?",
    "What's my upcoming RSU vest schedule for the year?",
    "What would I owe in tax if I sold all my vested RSUs?",
  ],
  family: [
    "How old is each family member?",
    "What ages does my daughter hit Phase 16 features?",
    "Whose birthday or work anniversary is coming up?",
  ],
  allowance: [
    "How much has my daughter earned this year?",
    "Which chores are paying out the most?",
    "What's the bucket split most kids in our peer group use?",
  ],
  buckets: [
    "How much is in each of my daughter's buckets?",
    "Has my daughter spent more from Spend or Save lately?",
    "What's the right way to think about Give bucket allocations?",
  ],
  banking: [
    "Which HYSA has the best APY for my balance?",
    "What's my optimal CD ladder schedule?",
    "How should I structure I-Bond purchases this year?",
  ],
  watchlists: [
    "What's on my watchlist?",
    "Am I tracking any tickers that are also in my portfolio?",
  ],
  household: [
    "Who has access to this household?",
    "What roles does each member have?",
  ],
  settingsAi: [
    "How much have I spent on AI this month?",
    "Am I close to my monthly token cap?",
    "Compare costs across providers.",
  ],
  // Phase 13.5: FIRE elevation — sub-page-scoped prompt suggestions.
  // (`fire` + `fireContributionPriority` keys already exist above.)
  fireNumbers: [
    "Walk me through Shockingly Simple Math for my numbers.",
    "What does my FI date look like at 4% vs 3.5% withdrawal?",
    "Compare my Lean FI and Coast FI dates.",
  ],
  fireWithdrawal: [
    "What's the worst-case withdrawal rate my portfolio survived in the historical sequences?",
    "How does Guyton-Klinger compare to constant 4% for my situation?",
    "What's my safe withdrawal rate at 95% Monte Carlo confidence?",
  ],
  fireSavingRate: [
    "How has my saving rate trended over the last 12 months?",
    "Why is my saving rate down from last year?",
    "Where in 'Shockingly Simple Math' does my current rate land me?",
  ],
  fireSocialSecurity: [
    "When should I claim Social Security to maximize lifetime benefit?",
    "What does my benefit look like at 62 vs 67 vs 70?",
    "How does my SS benefit affect my FI target?",
  ],
  fireGeographicArbitrage: [
    "Which lower-cost cities would extend my retirement runway most?",
    "How would moving to Austin change my FI math?",
    "What's the COL multiplier for the cities I'm considering?",
  ],
  // Phase 13.5: settings tray surface.
  settingsEmails: [
    "What emails does Hearth send me?",
    "How do I opt out of the lifestyle inflation digest?",
    "Can my spouse get the monthly summary instead of me?",
  ],
} as const;

export type PageKey = keyof typeof PAGE_PROMPTS;
